'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import type { InferContractRouterOutputs } from '@orpc/contract';
import type { Address } from 'viem';
import { Card } from '@morpho-org/ui-v6/server';
import { SuspenseErrorBoundary } from '@repo/ui';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useState, useTransition } from 'react';
import { parseEther } from 'viem';
import type { Contract } from '@/lib/orpc/contracts';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { getTimeFrameOptionsForVaultAge, vaultV2DefaultTimeFrame } from '@/config/table';
import type { VaultHistoryMode } from './hooks/useCurrencyMode';
import { ChartHeader } from './components';
import { ChartControls } from './components';
import { useCurrencyMode, useFormattedValues, useTotalDepositTimeseriesData } from './hooks';
import { hasNoChartValues } from './toTotalDepositChartData';
export interface VaultV2ChartTotalDepositClientProps {
  chainId: number;
  address: Address;
  totalAssets: InferContractRouterOutputs<Contract>['vaultV2']['totalAssets'];
  now: number;
}

/**
 * Chart component to display the vault deposit
 */
export default function VaultV2ChartTotalDepositClient(t0) {
  const $ = _c(41);
  const {
    chainId,
    address,
    totalAssets,
    now
  } = t0;
  const {
    asset
  } = totalAssets;
  const decimals = asset.decimals ?? undefined;
  const {
    currency,
    setCurrency,
    dataMode,
    currencyOptions
  } = useCurrencyMode(asset.symbol ?? undefined);
  let t1;
  if ($[0] !== now || $[1] !== totalAssets.creationTimestamp) {
    t1 = getTimeFrameOptionsForVaultAge(totalAssets.creationTimestamp, now);
    $[0] = now;
    $[1] = totalAssets.creationTimestamp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const timeFrameOptions = t1;
  const [timeFrame, setTimeFrameState] = useState(vaultV2DefaultTimeFrame);
  const [isRangeLoading, startTransition] = useTransition();
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = next => {
      startTransition(() => setTimeFrameState(next));
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const setTimeFrame = t2;
  let t3;
  if ($[4] !== decimals || $[5] !== totalAssets.totalAssets) {
    t3 = totalAssets.totalAssets === null || decimals === undefined ? null : BigInt(totalAssets.totalAssets);
    $[4] = decimals;
    $[5] = totalAssets.totalAssets;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = asset.symbol ?? undefined;
  let t5;
  if ($[7] !== asset.priceUsd) {
    t5 = asset.priceUsd === null ? undefined : parseEther(asset.priceUsd.toFixed(18));
    $[7] = asset.priceUsd;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== decimals || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      symbol: t4,
      decimals,
      price: t5
    };
    $[9] = decimals;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== dataMode || $[14] !== t3 || $[15] !== t6 || $[16] !== totalAssets.totalAssetsUsd) {
    t7 = {
      dataMode,
      totalAssets: t3,
      totalAssetsUsd: totalAssets.totalAssetsUsd,
      asset: t6
    };
    $[13] = dataMode;
    $[14] = t3;
    $[15] = t6;
    $[16] = totalAssets.totalAssetsUsd;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const formattedValues = useFormattedValues(t7);
  const unit = currency === "USD" ? "$" : currency;
  let t8;
  if ($[18] !== currency || $[19] !== formattedValues) {
    t8 = <ChartHeader currency={currency} formattedValues={formattedValues} />;
    $[18] = currency;
    $[19] = formattedValues;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== currency || $[22] !== currencyOptions || $[23] !== setCurrency || $[24] !== timeFrame || $[25] !== timeFrameOptions) {
    t9 = <ChartControls currency={currency} currencyOptions={currencyOptions} onCurrencyChange={setCurrency} timeFrame={timeFrame} onTimeFrameChange={setTimeFrame} timeFrameOptions={timeFrameOptions} />;
    $[21] = currency;
    $[22] = currencyOptions;
    $[23] = setCurrency;
    $[24] = timeFrame;
    $[25] = timeFrameOptions;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <div className="gap-sm flex flex-wrap items-center justify-between">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== address || $[31] !== asset.decimals || $[32] !== chainId || $[33] !== dataMode || $[34] !== isRangeLoading || $[35] !== timeFrame || $[36] !== unit) {
    t11 = <QueryErrorResetBoundary>{t12 => {
        const {
          reset
        } = t12;
        return <SuspenseErrorBoundary resetKeys={[timeFrame]} onReset={reset} fallback={<TotalDepositChart unit={unit} data={[]} statuses={{
          loading: true
        }} />} errorFallback={<TotalDepositChart unit={unit} data={[]} statuses={{
          loading: false,
          error: true
        }} />}><TotalDepositSeries chainId={chainId} address={address} timeFrame={timeFrame} dataMode={dataMode} assetDecimals={asset.decimals} unit={unit} loading={isRangeLoading} /></SuspenseErrorBoundary>;
      }}</QueryErrorResetBoundary>;
    $[30] = address;
    $[31] = asset.decimals;
    $[32] = chainId;
    $[33] = dataMode;
    $[34] = isRangeLoading;
    $[35] = timeFrame;
    $[36] = unit;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t10 || $[39] !== t11) {
    t12 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" withBorder={true} data-testid="vault-v2-total-deposits">{t10}{t11}</Card>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}
interface TotalDepositSeriesProps {
  chainId: number;
  address: Address;
  timeFrame: Time.PeriodLike | null;
  dataMode: VaultHistoryMode;
  assetDecimals: number | null;
  unit: string;
  loading: boolean;
}
function TotalDepositSeries(t0) {
  const $ = _c(15);
  const {
    chainId,
    address,
    timeFrame,
    dataMode,
    assetDecimals,
    unit,
    loading
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== assetDecimals || $[2] !== chainId || $[3] !== dataMode || $[4] !== timeFrame) {
    t1 = {
      chainId,
      address,
      timeFrame,
      dataMode,
      assetDecimals
    };
    $[0] = address;
    $[1] = assetDecimals;
    $[2] = chainId;
    $[3] = dataMode;
    $[4] = timeFrame;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const data = useTotalDepositTimeseriesData(t1);
  let t2;
  if ($[6] !== data) {
    t2 = hasNoChartValues(data);
    $[6] = data;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== loading || $[9] !== t2) {
    t3 = {
      loading,
      error: t2
    };
    $[8] = loading;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== data || $[12] !== t3 || $[13] !== unit) {
    t4 = <TotalDepositChart unit={unit} data={data} statuses={t3} />;
    $[11] = data;
    $[12] = t3;
    $[13] = unit;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
interface TotalDepositChartProps {
  unit: string;
  data: [number, number | null][];
  statuses: {
    loading: boolean;
    error?: boolean;
  };
}
function TotalDepositChart(t0) {
  const $ = _c(9);
  const {
    unit,
    data,
    statuses
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  let t1;
  if ($[0] !== data || $[1] !== palette.areaChartColors[0] || $[2] !== palette.colors[0]) {
    t1 = [{
      type: "areaspline",
      name: "Total Deposits",
      data,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[0] = data;
    $[1] = palette.areaChartColors[0];
    $[2] = palette.colors[0];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== statuses || $[6] !== t1 || $[7] !== unit) {
    t3 = <SimpleChart statuses={statuses} unit={unit} height="224px" series={t1} additionalOptions={t2} />;
    $[5] = statuses;
    $[6] = t1;
    $[7] = unit;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}